/**
 * @lodex-pro/web-shared/api/api-opts-context — React context for WebApiOpts.
 *
 * Phase 9 / Plan 09-03 — provides the API base URL to feature hooks without
 * prop-drilling. Consumer apps mount `<ApiOptsProvider opts={{ baseUrl: ... }}>` at
 * their root (alongside QueryProvider), and feature hooks call `useApiOpts()` to
 * get the base URL they pass to `authedFetch` via `useApiQuery`/`useApiMutation`.
 *
 * Plan 09-04 wires: `ApiOptsProvider opts={{ baseUrl: API_BASE_URL }}` in main.tsx.
 *
 * Spec: CONTEXT D-06 (web foundation); RESEARCH Pattern 1 (authedFetch contract).
 */

import * as React from 'react';
import type { WebApiOpts } from '../auth/api-client.js';

const ApiOptsContext = React.createContext<WebApiOpts | null>(null);

export interface ApiOptsProviderProps {
  /** API configuration (base URL). Injected once at the app root by the consumer. */
  readonly opts: WebApiOpts;
  readonly children: React.ReactNode;
}

/**
 * Provides `WebApiOpts` to the React subtree. Mount once at the consumer app root,
 * above any component or hook that calls `useApiQuery` or `useApiMutation`.
 */
export function ApiOptsProvider({ opts, children }: ApiOptsProviderProps): React.JSX.Element {
  return React.createElement(ApiOptsContext.Provider, { value: opts }, children);
}

/**
 * Returns the `WebApiOpts` from the nearest `ApiOptsProvider`.
 *
 * @throws {Error} if called outside an `ApiOptsProvider` — prevents silent failures
 * where feature hooks silently call `authedFetch` with an undefined base URL.
 */
export function useApiOpts(): WebApiOpts {
  const ctx = React.useContext(ApiOptsContext);
  if (ctx === null) {
    throw new Error(
      'useApiOpts() called outside <ApiOptsProvider>. ' +
        'Wrap your app root with <ApiOptsProvider opts={{ baseUrl: ... }}>.',
    );
  }
  return ctx;
}

/**
 * OPEN-109 — like `useApiOpts`, but returns null outside a provider instead of throwing. For
 * components that can do something sensible without the API (the sign-in help line falls back
 * to the default support address).
 */
export function useApiOptsOrNull(): WebApiOpts | null {
  return React.useContext(ApiOptsContext);
}
